import { useNavigate } from 'react-router-dom'
import { Store } from 'lucide-react'
import { STRIPE } from '@/data/mockData'
import { useFollowedBusinesses, useToggleFollow } from '@/hooks/useBusiness'

const C = {
  teal: '#0F5C5C',
  terra: '#D9785B',
  ink2: '#6D7471',
  border: '#E1E5E3',
  grayBg: '#F1F3F2',
}
const FH = "'Plus Jakarta Sans', sans-serif"
const FB = "'Inter', sans-serif"

interface FollowedBusiness {
  id: string
  name: string
  category: string
  zone: string
  emoji: string
  cover_colors: [string, string]
  verified: boolean
  is_open: boolean
  follower_count: number
}

interface Props {
  /** Se dispara justo antes de navegar (ej. para cerrar una ventana flotante). */
  onNavigate?: () => void
  /** Filtra la lista por nombre del negocio (búsqueda). */
  filter?: string
}

export default function FollowedBusinessesList({ onNavigate, filter }: Props) {
  const navigate = useNavigate()
  const { data, isLoading } = useFollowedBusinesses()
  const businesses = (data?.data ?? []) as unknown as FollowedBusiness[]

  const q = (filter ?? '').trim().toLowerCase()
  const shown = q ? businesses.filter((b) => b.name.toLowerCase().includes(q)) : businesses

  if (isLoading) return null

  if (businesses.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px' }}>
        <div
          style={{
            width: 60,
            height: 60,
            borderRadius: 16,
            background: C.grayBg,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 14,
          }}
        >
          <Store size={26} color={C.ink2} />
        </div>
        <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 17, marginBottom: 8 }}>
          Aún no sigues ningún negocio
        </div>
        <div style={{ fontSize: 13.5, color: C.ink2, lineHeight: 1.5, marginBottom: 18 }}>
          Entra al perfil de un negocio y toca "Seguir" para verlo aquí.
        </div>
        <button
          onClick={() => {
            onNavigate?.()
            navigate('/')
          }}
          style={{
            background: C.teal,
            color: '#fff',
            border: 'none',
            borderRadius: 11,
            padding: '10px 20px',
            fontFamily: FB,
            fontWeight: 700,
            fontSize: 13.5,
            cursor: 'pointer',
          }}
        >
          Explorar negocios
        </button>
      </div>
    )
  }

  if (shown.length === 0) {
    return (
      <div style={{ fontSize: 13.5, color: C.ink2, padding: '20px 4px' }}>
        No sigues ningún negocio que coincida con "{filter}".
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {shown.map((b) => (
        <FollowedRow key={b.id} business={b} onNavigate={onNavigate} />
      ))}
    </div>
  )
}

function FollowedRow({
  business,
  onNavigate,
}: {
  business: FollowedBusiness
  onNavigate?: () => void
}) {
  const navigate = useNavigate()
  const toggleFollow = useToggleFollow(business.id)

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 14,
        padding: '14px 16px',
      }}
    >
      <button
        onClick={() => {
          onNavigate?.()
          navigate(`/biz/${business.id}`)
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flex: 1,
          minWidth: 0,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
          padding: 0,
        }}
      >
        <div
          style={{
            width: 50,
            height: 50,
            borderRadius: 13,
            background: STRIPE(business.cover_colors[0], business.cover_colors[1]),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 22,
            flexShrink: 0,
          }}
        >
          {business.emoji}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span
              style={{
                fontFamily: FH,
                fontWeight: 700,
                fontSize: 15.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {business.name}
            </span>
            {business.verified && <span style={{ color: C.teal, fontSize: 13 }}>✓</span>}
          </div>
          <div
            style={{
              fontSize: 13,
              color: C.ink2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {business.category} · {business.zone}
          </div>
        </div>
      </button>

      <button
        onClick={() => toggleFollow.mutate(false)}
        style={{
          flexShrink: 0,
          padding: '9px 16px',
          borderRadius: 10,
          border: `1px solid ${C.terra}`,
          background: '#fff',
          color: C.terra,
          fontFamily: FB,
          fontWeight: 700,
          fontSize: 13,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        Dejar de seguir
      </button>
    </div>
  )
}
